/**
 * Fias Leads — global cross-entity search box (lives in the arche header).
 *
 * Type a name and get matching contacts, companies, and deals in one
 * dropdown. The motivating case: you remember a person but not which deal
 * they're on — search their name and the contact row shows their company
 * and the deals they're attached to, each clickable straight to its detail
 * page.
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Users, Building2, TrendingUp, Loader2 } from 'lucide-react';
import { ARCHE_PATH } from '../constants';
import { useFiasLeadsGlobalSearch, FIAS_LEADS_SEARCH_MIN_LENGTH } from '../hooks/useFiasLeads';
import type {
  FiasLeadsSearchContactResultDto,
  FiasLeadsSearchCompanyResultDto,
  FiasLeadsSearchDealResultDto,
  FiasLeadsSearchDealRefDto,
} from '@/lib/api';

const DEBOUNCE_MS = 250;

function contactName(c: { firstName: string; lastName: string | null }): string {
  return `${c.firstName} ${c.lastName ?? ''}`.trim();
}

function formatValue(value: number, currency: string): string {
  if (!value) return '';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${value}`;
  }
}

interface FiasLeadsGlobalSearchProps {
  holderId: string;
}

export function FiasLeadsGlobalSearch({ holderId }: FiasLeadsGlobalSearchProps) {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  // Debounce the network query independently of the controlled input so
  // typing stays responsive but we don't fire a request per keystroke.
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(input.trim()), DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [input]);

  const { data, isFetching } = useFiasLeadsGlobalSearch(holderId, debounced);

  // Close on outside click.
  useEffect(() => {
    function onPointerDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const tooShort = input.trim().length < FIAS_LEADS_SEARCH_MIN_LENGTH;
  const showPanel = open && !tooShort;

  const counts = useMemo(() => {
    if (!data) return { total: 0 };
    return {
      total: data.contacts.length + data.companies.length + data.deals.length,
    };
  }, [data]);

  function go(path: string) {
    setOpen(false);
    setInput('');
    setDebounced('');
    navigate(path);
  }

  function clear() {
    setInput('');
    setDebounced('');
  }

  return (
    <div ref={containerRef} className="relative w-64 max-w-[60vw]">
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
          aria-hidden="true"
        />
        <input
          type="text"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false);
          }}
          placeholder="Search contacts, companies, deals…"
          aria-label="Search Fias Leads"
          role="combobox"
          aria-expanded={showPanel}
          aria-controls={listboxId}
          className="w-full rounded-md border border-border bg-background py-2 pl-8 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
        />
        {input && (
          <button
            type="button"
            onClick={clear}
            aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {showPanel && (
        <div
          id={listboxId}
          role="listbox"
          className="absolute right-0 z-50 mt-2 max-h-[70vh] w-[28rem] max-w-[90vw] overflow-y-auto rounded-md border border-border bg-card shadow-xl"
        >
          {isFetching && !data && (
            <div className="flex items-center gap-2 px-4 py-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Searching…
            </div>
          )}

          {data && counts.total === 0 && !isFetching && (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">
              No matches for “{debounced}”.
            </div>
          )}

          {data && data.contacts.length > 0 && (
            <Group label="Contacts" icon={Users}>
              {data.contacts.map((c) => (
                <ContactRow
                  key={c.contactId}
                  contact={c}
                  onOpenContact={() => go(`${ARCHE_PATH}/contacts/${c.contactId}`)}
                  onOpenCompany={() => c.companyId && go(`${ARCHE_PATH}/companies/${c.companyId}`)}
                  onOpenDeal={(d) => go(`${ARCHE_PATH}/deals/${d.id}`)}
                />
              ))}
            </Group>
          )}

          {data && data.companies.length > 0 && (
            <Group label="Companies" icon={Building2}>
              {data.companies.map((co) => (
                <CompanyRow
                  key={co.companyId}
                  company={co}
                  onOpen={() => go(`${ARCHE_PATH}/companies/${co.companyId}`)}
                />
              ))}
            </Group>
          )}

          {data && data.deals.length > 0 && (
            <Group label="Deals" icon={TrendingUp}>
              {data.deals.map((d) => (
                <DealRow key={d.id} deal={d} onOpen={() => go(`${ARCHE_PATH}/deals/${d.id}`)} />
              ))}
            </Group>
          )}
        </div>
      )}
    </div>
  );
}

function Group({
  label,
  icon: Icon,
  children,
}: {
  label: string;
  icon: typeof Users;
  children: React.ReactNode;
}) {
  return (
    <div className="border-b border-border last:border-b-0">
      <div className="flex items-center gap-2 px-3 pt-2 pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        {label}
      </div>
      <ul className="pb-1">{children}</ul>
    </div>
  );
}

function ContactRow({
  contact,
  onOpenContact,
  onOpenCompany,
  onOpenDeal,
}: {
  contact: FiasLeadsSearchContactResultDto;
  onOpenContact: () => void;
  onOpenCompany: () => void;
  onOpenDeal: (deal: FiasLeadsSearchDealRefDto) => void;
}) {
  return (
    <li className="px-1.5">
      <button
        type="button"
        role="option"
        aria-selected={false}
        onClick={onOpenContact}
        className="w-full rounded-md px-2.5 py-1.5 text-left hover:bg-muted"
      >
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-sm font-medium text-foreground">
            {contactName(contact)}
          </span>
          {contact.title && (
            <span className="truncate text-xs text-muted-foreground">{contact.title}</span>
          )}
        </div>
        {contact.primaryEmail && (
          <div className="truncate text-xs text-muted-foreground">{contact.primaryEmail}</div>
        )}
      </button>
      {/* Related records — the point of the feature. */}
      {(contact.companyName || contact.deals.length > 0) && (
        <div className="flex flex-wrap gap-1 px-2.5 pb-1.5">
          {contact.companyName && (
            <Chip
              icon={Building2}
              label={contact.companyName}
              onClick={(e) => {
                e.stopPropagation();
                onOpenCompany();
              }}
            />
          )}
          {contact.deals.map((d) => (
            <Chip
              key={d.id}
              icon={TrendingUp}
              label={`${d.name} · ${d.stageName}`}
              onClick={(e) => {
                e.stopPropagation();
                onOpenDeal(d);
              }}
            />
          ))}
        </div>
      )}
    </li>
  );
}

function CompanyRow({
  company,
  onOpen,
}: {
  company: FiasLeadsSearchCompanyResultDto;
  onOpen: () => void;
}) {
  const meta = [
    company.industry,
    company.contactCount > 0 ? `${company.contactCount} contacts` : null,
    company.dealCount > 0 ? `${company.dealCount} deals` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <li className="px-1.5">
      <button
        type="button"
        role="option"
        aria-selected={false}
        onClick={onOpen}
        className="w-full rounded-md px-2.5 py-1.5 text-left hover:bg-muted"
      >
        <div className="truncate text-sm font-medium text-foreground">{company.name}</div>
        {meta && <div className="truncate text-xs text-muted-foreground">{meta}</div>}
      </button>
    </li>
  );
}

function DealRow({ deal, onOpen }: { deal: FiasLeadsSearchDealResultDto; onOpen: () => void }) {
  const value = formatValue(deal.value, deal.currency);
  const who = [deal.contactName, deal.companyName].filter(Boolean).join(' · ');
  const meta = [deal.stageName, value, who].filter(Boolean).join(' · ');
  return (
    <li className="px-1.5">
      <button
        type="button"
        role="option"
        aria-selected={false}
        onClick={onOpen}
        className="w-full rounded-md px-2.5 py-1.5 text-left hover:bg-muted"
      >
        <div className="truncate text-sm font-medium text-foreground">{deal.name}</div>
        {meta && <div className="truncate text-xs text-muted-foreground">{meta}</div>}
      </button>
    </li>
  );
}

function Chip({
  icon: Icon,
  label,
  onClick,
}: {
  icon: typeof Users;
  label: string;
  onClick: (e: React.MouseEvent) => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex max-w-[14rem] items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
    >
      <Icon className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
      <span className="truncate">{label}</span>
    </button>
  );
}
